import React, {
    FC,
    ReactNode,
    useEffect,
    useRef,
    useState,
    MouseEvent as ReactMouseEvent,
} from 'react'
import styled from 'styled-components'

import { UpButton, DownButton, LeftButton, RightButton } from './scrollButton'
import {
    HorizontalScrollbar,
    HorizontalThumb,
    HorizontalTrackAndThumb,
    HorizontalTrack,
} from './horizontalScroll.styled'
import {
    VerticalScrollbar,
    VerticalThumb,
    VerticalTrack,
    VerticalTrackAndThumb,
} from './verticalScroll.styled'
import {
    useOnVerticalThumbMouseDown,
    useOnVerticalThumbMouseMove,
    useOnVerticalThumbMouseUp,
    useOnVerticalThumbMouseWheelScroll,
    useOnVerticalTrackClick,
} from './verticalScrollHooks'
import {
    useOnHorizontalThumbMove,
    useOnHorizontalTrackClick,
    useOnHorizontalThumbMouseDown,
    useOnHorizontalThumbMouseUp,
    useOnHorizontalThumbScroll,
} from './horizontalScrollHooks'
import { ComponentHeights } from './componentHeightConstants'

// const scrollbarWidth = 12

const Container = styled.div<{
    $width?: string
    $maxHeight?: string
    $maxWidth?: string
    $scrollbarWidth?: number
    $hasHorizontalOverflow: boolean
    $hasVerticalOverflow: boolean
    $verticalScrollBarLeftPadding: number
}>`
    width: ${(p) => p.$width ?? '100%'};
    height: 100%;
    max-width: ${(p) => p.$maxWidth ?? '100%'};
    max-height: ${(p) => p.$maxHeight ?? '100%'};

    display: grid;
    grid-template-rows: 1fr ${(p) =>
            p.$hasHorizontalOverflow
                ? p.$scrollbarWidth
                    ? p.$scrollbarWidth + 4
                    : 8
                : 0}px;

    grid-template-columns: 1fr ${(p) =>
            p.$hasVerticalOverflow
                ? p.$scrollbarWidth
                    ? p.$scrollbarWidth + 4 + p.$verticalScrollBarLeftPadding
                    : 8
                : 0}px;

    overflow: hidden;
    position: relative;
    box-sizing: border-box;
`

const Content = styled.div`
    -ms-overflow-style: none;
    overflow: auto;
    scrollbar-width: none;
    height: inherit;
    //padding: 0 1rem;

    &::-webkit-scrollbar {
        display: none;
    }
`

interface Props {
    children: ReactNode
    width?: string
    maxHeight?: string
    maxWidth?: string
    offsets?: ComponentHeights[]
    scrollbarWidth?: number
    showActionButtons?: boolean
    verticalScrollBarVerticalPadding?: number
    verticalScrollBarLeftPadding?: number
    className?: string
}

export const Scroll: FC<Props> = ({
    children,
    width,
    maxHeight,
    maxWidth,
    offsets = [],
    scrollbarWidth = 8,
    showActionButtons,
    verticalScrollBarVerticalPadding = 2,
    verticalScrollBarLeftPadding = 0,
    className,
}) => {
    const contentRef = useRef<HTMLDivElement>(null)
    const observer = useRef<ResizeObserver | null>(null)

    const verticalScrollTrackRef = useRef<HTMLDivElement>(null)
    const verticalScrollThumbRef = useRef<HTMLDivElement>(null)
    const horizontalScrollTrackRef = useRef<HTMLDivElement>(null)
    const horizontalScrollThumbRef = useRef<HTMLDivElement>(null)

    // Vertical scroll states
    const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false)
    const [verticalScrollStartPosition, setVerticalScrollStartPosition] =
        useState(0)
    const [isVerticalThumbDragging, setIsVerticalThumbDragging] =
        useState(false)
    const [verticalThumbHeight, setVerticalThumbHeight] = useState(20)
    const [initialContentScrollTop, setInitialContentScrollTop] = useState(0)

    // Horizontal scroll states
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)
    const [horizontalScrollStartPosition, setHorizontalScrollStartPosition] =
        useState(0)
    const [isHorizontalThumbDragging, setIsHorizontalThumbDragging] =
        useState(false)
    const [horizontalThumbWidth, setHorizontalThumbWidth] = useState(20)
    const [initialContentScrollLeft, setInitialContentScrollLeft] = useState(0)

    // Vertical event handling
    const onVerticalTrackClick = useOnVerticalTrackClick(
        verticalScrollTrackRef,
        contentRef,
        verticalThumbHeight
    )
    const onVerticalThumbMouseMove = useOnVerticalThumbMouseMove(
        contentRef,
        isVerticalThumbDragging,
        verticalScrollStartPosition,
        initialContentScrollTop,
        verticalThumbHeight
    )
    const onVerticalThumbMouseWheelScroll = useOnVerticalThumbMouseWheelScroll(
        contentRef,
        verticalScrollTrackRef,
        verticalScrollThumbRef,
        verticalThumbHeight
    )
    const onVerticalThumbMouseUp = useOnVerticalThumbMouseUp(
        isVerticalThumbDragging,
        setIsVerticalThumbDragging
    )
    const onVerticalThumbMouseDown = useOnVerticalThumbMouseDown(
        contentRef,
        setVerticalScrollStartPosition,
        setInitialContentScrollTop,
        setIsVerticalThumbDragging
    )

    // Horizontal event handling
    const onHorizontalTrackClick = useOnHorizontalTrackClick(
        horizontalScrollTrackRef,
        contentRef,
        horizontalThumbWidth
    )
    const onHorizontalThumbMouseMove = useOnHorizontalThumbMove(
        contentRef,
        isHorizontalThumbDragging,
        horizontalScrollStartPosition,
        initialContentScrollLeft,
        horizontalThumbWidth
    )
    const onHorizontalThumbMouseWheelScroll = useOnHorizontalThumbScroll(
        contentRef,
        horizontalScrollTrackRef,
        horizontalScrollThumbRef,
        horizontalThumbWidth
    )
    const onHorizontalThumbMouseUp = useOnHorizontalThumbMouseUp(
        isHorizontalThumbDragging,
        setIsHorizontalThumbDragging
    )
    const onHorizontalThumbMouseDown = useOnHorizontalThumbMouseDown(
        contentRef,
        setHorizontalScrollStartPosition,
        setInitialContentScrollLeft,
        setIsHorizontalThumbDragging
    )

    const onResize = () => {
        // Change vertical thumb height
        if (contentRef.current && verticalScrollTrackRef.current) {
            const { clientHeight: trackHeight } = verticalScrollTrackRef.current
            const {
                clientHeight: contentVisibleHeight,
                scrollHeight: contentTotalHeight,
            } = contentRef.current

            setVerticalThumbHeight(
                Math.max(
                    (contentVisibleHeight / contentTotalHeight) * trackHeight,
                    20
                )
            )
        }

        // Change horizontal thumb width
        if (contentRef.current && horizontalScrollTrackRef.current) {
            const { clientWidth: trackWidth } = horizontalScrollTrackRef.current
            const {
                clientWidth: contentVisibleWidth,
                scrollWidth: contentTotalWidth,
            } = contentRef.current

            setHorizontalThumbWidth(
                Math.max(
                    (contentVisibleWidth / contentTotalWidth) * trackWidth,
                    20
                )
            )
        }
    }

    // const onThumbMouseMove = (event: ReactMouseEvent) => {
    //     // TODO: (FZ) Find a way to know if the event is horizontal or vertical event
    //     if (event.shiftKey) {
    //         onHorizontalThumbMouseMove(event)
    //     } else onVerticalThumbMouseMove(event)
    // }

    const height = `calc(${maxHeight} - ${offsets.reduce((sum, offset) => sum + offset, 0)}px)`

    const onButtonClick = (direction: 'up' | 'down' | 'left' | 'right') => {
        const { current: content } = contentRef
        if (content) {
            const scrollAmount =
                direction === 'down' || direction === 'right' ? 100 : -100

            if (direction === 'up' || direction === 'down') {
                content.scrollBy({ top: scrollAmount, behavior: 'smooth' })
            } else if (direction === 'left' || direction === 'right') {
                content.scrollBy({ left: scrollAmount, behavior: 'smooth' })
            }
        }
    }

    useEffect(() => {
        if (contentRef.current) {
            const content = contentRef.current
            observer.current = new ResizeObserver(onResize)
            observer.current.observe(content)
            content.addEventListener(
                'scroll',
                onHorizontalThumbMouseWheelScroll
            )
            content.addEventListener('scroll', onVerticalThumbMouseWheelScroll)

            return () => {
                observer.current?.unobserve(content)
                content.removeEventListener(
                    'scroll',
                    onHorizontalThumbMouseWheelScroll
                )
                content.removeEventListener(
                    'scroll',
                    onVerticalThumbMouseWheelScroll
                )
            }
        }
    }, [])

    useEffect(() => {
        const verticalThumbMouseMoveListener = (event: MouseEvent) =>
            onVerticalThumbMouseMove(event as unknown as ReactMouseEvent)
        const verticalThumbMouseUpListener = (event: MouseEvent) =>
            onVerticalThumbMouseUp(event as unknown as ReactMouseEvent)
        const horizontalThumbMouseMoveListener = (event: MouseEvent) =>
            onHorizontalThumbMouseMove(event as unknown as ReactMouseEvent)
        const horizontalThumbMouseUpListener = (event: MouseEvent) =>
            onHorizontalThumbMouseUp(event as unknown as ReactMouseEvent)

        document.addEventListener('mousemove', verticalThumbMouseMoveListener)
        document.addEventListener('mouseup', verticalThumbMouseUpListener)
        document.addEventListener('mousemove', horizontalThumbMouseMoveListener)
        document.addEventListener('mouseup', horizontalThumbMouseUpListener)

        return () => {
            document.removeEventListener(
                'mousemove',
                verticalThumbMouseMoveListener
            )
            document.removeEventListener(
                'mouseup',
                verticalThumbMouseUpListener
            )
            document.removeEventListener(
                'mousemove',
                horizontalThumbMouseMoveListener
            )
            document.removeEventListener(
                'mouseup',
                horizontalThumbMouseUpListener
            )
        }
    }, [
        onVerticalThumbMouseMove,
        onVerticalThumbMouseUp,
        onHorizontalThumbMouseMove,
        onHorizontalThumbMouseUp,
    ])

    useEffect(() => {
        if (!contentRef?.current) return

        const {
            clientWidth: contentVisibleWidth,
            scrollWidth: contentTotalWidth,
        } = contentRef.current

        setHasHorizontalOverflow(contentTotalWidth > contentVisibleWidth)
        onResize()
    }, [
        children,
        contentRef?.current?.scrollWidth,
        contentRef?.current?.clientWidth,
    ])

    useEffect(() => {
        if (!contentRef?.current) {
            return
        }

        const {
            clientHeight: contentVisibleHeight,
            scrollHeight: contentTotalHeight,
        } = contentRef.current

        setHasVerticalOverflow(contentTotalHeight > contentVisibleHeight)
        onResize()
    }, [
        // children,
        // contentRef?.current,
        contentRef?.current?.scrollHeight,
        contentRef?.current?.clientHeight,
    ])

    return (
        <Container
            className={className}
            $width={width}
            $maxHeight={height}
            $maxWidth={maxWidth}
            $scrollbarWidth={scrollbarWidth}
            $hasHorizontalOverflow={hasHorizontalOverflow}
            $hasVerticalOverflow={hasVerticalOverflow}
            $verticalScrollBarLeftPadding={verticalScrollBarLeftPadding}
        >
            <Content ref={contentRef}>{children}</Content>

            {hasVerticalOverflow && (
                <VerticalScrollbar
                    $hasOverflow={hasVerticalOverflow}
                    $showActionButtons={showActionButtons}
                    $verticalScrollBarVerticalPadding={
                        verticalScrollBarVerticalPadding
                    }
                    $verticalScrollBarLeftPadding={verticalScrollBarLeftPadding}
                >
                    {showActionButtons ? (
                        <UpButton
                            $size={scrollbarWidth}
                            onClick={() => onButtonClick('up')}
                        >
                            ↑
                        </UpButton>
                    ) : (
                        <div></div>
                    )}

                    <VerticalTrackAndThumb $scrollbarWidth={scrollbarWidth}>
                        <VerticalTrack
                            ref={verticalScrollTrackRef}
                            $isDragging={isVerticalThumbDragging}
                            $scrollbarWidth={scrollbarWidth}
                            onClick={onVerticalTrackClick}
                        />
                        <VerticalThumb
                            ref={verticalScrollThumbRef}
                            $height={verticalThumbHeight}
                            $isDragging={isVerticalThumbDragging}
                            $scrollbarWidth={scrollbarWidth}
                            onMouseDown={onVerticalThumbMouseDown}
                        />
                    </VerticalTrackAndThumb>

                    {showActionButtons ? (
                        <DownButton
                            $size={scrollbarWidth}
                            onClick={() => onButtonClick('down')}
                        >
                            ↓
                        </DownButton>
                    ) : (
                        <div></div>
                    )}
                </VerticalScrollbar>
            )}

            {hasHorizontalOverflow && (
                <HorizontalScrollbar
                    $hasOverflow={hasHorizontalOverflow}
                    $showActionButtons={showActionButtons}
                >
                    {showActionButtons ? (
                        <LeftButton
                            $size={scrollbarWidth}
                            onClick={() => onButtonClick('left')}
                        >
                            ←
                        </LeftButton>
                    ) : (
                        <div></div>
                    )}

                    <HorizontalTrackAndThumb $scrollbarWidth={scrollbarWidth}>
                        <HorizontalTrack
                            ref={horizontalScrollTrackRef}
                            $scrollbarWidth={scrollbarWidth}
                            $isDragging={isHorizontalThumbDragging}
                            onClick={onHorizontalTrackClick}
                        />
                        <HorizontalThumb
                            ref={horizontalScrollThumbRef}
                            $width={horizontalThumbWidth}
                            $isDragging={isHorizontalThumbDragging}
                            $scrollbarWidth={scrollbarWidth}
                            onMouseDown={onHorizontalThumbMouseDown}
                        />
                    </HorizontalTrackAndThumb>
                    {showActionButtons ? (
                        <RightButton
                            $size={scrollbarWidth}
                            onClick={() => onButtonClick('right')}
                        >
                            →
                        </RightButton>
                    ) : (
                        <div></div>
                    )}
                </HorizontalScrollbar>
            )}
        </Container>
    )
}
